import React, { useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { LensCardType } from '~/types/leLens';

import ChartTypeGlyph from '../ChartTypeGlyph';

import TitleField from './tabs/TitleField';

export type CardStripProps = {
  cardType: LensCardType;
  title: string;
  onTitleChange: (title: string) => void;
  disabled?: boolean;
  /** Lets the panel move focus here the moment it opens at desktop widths,
   *  instead of leaving it stranded outside the pane entirely. */
  titleInputRef?: React.Ref<HTMLInputElement>;
  /** Lifted straight from the canvas card's own kebab menu
   *  (`LensCardShell.tsx`): the same handlers, a second entry point, not a
   *  parallel implementation. Absent hides the matching menu item, same as
   *  the canvas menu already does for a viewer or a card with nothing to
   *  duplicate/move into. */
  onDelete?: () => void;
  onDuplicate?: () => void;
  moveOptions?: Array<{ label: string; onMove: () => void }>;
};

/**
 * The header's title row: this card's shape, its editable name, and the
 * three actions that used to require closing the pane to reach (Duplicate,
 * Move to section, Delete, all still driven by the canvas card's own
 * handlers). Sits between the header's left edge and its Close button, so
 * the caller supplies no width or right-hand spacing of its own.
 */
export default function CardStrip({
  cardType,
  disabled,
  moveOptions,
  onDelete,
  onDuplicate,
  onTitleChange,
  title,
  titleInputRef,
}: CardStripProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const hasMenu = Boolean(onDelete || onDuplicate || moveOptions?.length);
  const closeMenu = () => setMenuAnchor(null);

  return (
    <Stack
      alignItems="center"
      direction="row"
      spacing={1}
      sx={{
        flex: 1,
        minWidth: 0,
      }}
    >
      <Box
        aria-hidden
        sx={{
          color: tokens.accent,
          display: 'inline-flex',
          flexShrink: 0,
          '& svg': { display: 'block', height: 20, width: 'auto' },
        }}
      >
        <ChartTypeGlyph type={cardType} />
      </Box>
      <TitleField
        disabled={disabled}
        inputRef={titleInputRef}
        onChange={onTitleChange}
        value={title}
        variant="plain"
      />
      {hasMenu && (
        <>
          <IconButton
            aria-controls={menuAnchor ? 'le-lens-card-strip-menu' : undefined}
            aria-haspopup="menu"
            aria-label="Card actions"
            disabled={disabled}
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            size="small"
            sx={{
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              color: menuAnchor ? 'text.primary' : 'text.secondary',
              flexShrink: 0,
              transition: 'color 120ms',
              '&:hover, &:focus-visible': { color: 'text.primary' },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu anchorEl={menuAnchor} id="le-lens-card-strip-menu" onClose={closeMenu} open={Boolean(menuAnchor)}>
            {moveOptions?.length
              ? [
                  ...moveOptions.map(({ label, onMove }) => (
                    <MenuItem
                      key={`move:${label}`}
                      onClick={() => {
                        closeMenu();
                        onMove();
                      }}
                    >
                      <ListItemIcon>
                        <DriveFileMoveOutlinedIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>{label}</ListItemText>
                    </MenuItem>
                  )),
                  <Divider key="move-divider" />,
                ]
              : null}
            {onDuplicate && (
              <MenuItem
                onClick={() => {
                  closeMenu();
                  onDuplicate();
                }}
              >
                <ListItemIcon>
                  <ContentCopyOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Duplicate card</ListItemText>
              </MenuItem>
            )}
            {onDelete && (
              <MenuItem
                onClick={() => {
                  closeMenu();
                  onDelete();
                }}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon sx={{ color: 'inherit' }}>
                  <DeleteOutlineIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Delete card</ListItemText>
              </MenuItem>
            )}
          </Menu>
        </>
      )}
    </Stack>
  );
}
